import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { dashboardCRUD, filialCRUD, filialDivisionCRUD, departmentCRUD } from "../../http";
import { monthStart, monthEnd } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Button, Stack, Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Divider } from "antd";
import { LoadingButton } from "@mui/lab";
import { sum } from "../../functions/linqSimulation";
import { roundTo2, formatDate, MOMENT_FORMAT_DATE_DMYHM_DOTS } from "../../functions/formatters";
import XLSX from "xlsx-js-style";
import { ContractSelect } from "../../components/Select/domainSelects";
import { organizationIds } from "../../models/organizationIds";

const safeRoundTo2 = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 0;
    }
    return roundTo2(value);
};

const DirectionsSummary = () => {
    const start = monthStart();
    const end = monthEnd();

    const [query, setQuery] = useState({
        paging: { skip: 0, take: 1000 },
        filter: {
            createdAt: {
                operand1: start,
                operand2: end,
                operator: "between",
            },
        },
    });

    const [filters, setFilters] = useState({
        governmentContractId: null,
        filialId: null,
        filialDivisionId: null,
        departmentId: null,
    });

    const [filials, setFilials] = useState([]);
    const [filialDivisions, setFilialDivisions] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [data, setData] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const fetchAsync = async () => {
        setLoading(true);
        const requestQuery = { ...query };

        if (filters.governmentContractId) {
            requestQuery.filter.contractId = {
                operand1: filters.governmentContractId,
                operator: "equals",
            };
        }
        if (filters.filialId) {
            requestQuery.filter.filialId = {
                operand1: filters.filialId,
                operator: "equals",
            };
        }
        if (filters.filialDivisionId) {
            requestQuery.filter.filialDivisionId = {
                operand1: filters.filialDivisionId,
                operator: "equals",
            };
        }
        if (filters.departmentId) {
            requestQuery.filter.departmentId = {
                operand1: filters.departmentId,
                operator: "equals",
            };
        }

        const response = await dashboardCRUD.post("directions/quantity", requestQuery);
        setData(response);
        const grouped = groupDataByFields(response);
        setGroupedData(grouped);
        setLoading(false);
    };

    const groupDataByFields = (data) => {
        const groups = {};
        data.forEach((item) => {
            const key = `${item.contractNumber || ""}_${item.filialName || ""}_${item.filialDivisionName || ""}_${item.createdAt || ""}_${item.directionNumber || ""}`;
            if (!groups[key]) {
                groups[key] = {
                    contractNumber: item.contractNumber || "",
                    governmentContractName: item.governmentContractName || "",
                    filialName: item.filialName || "",
                    filialDivisionName: item.filialDivisionName || "",
                    createdAt: item.createdAt,
                    directionNumber: item.directionNumber || "",
                    signedProtocols: 0,
                    departmentName: (item.departmentName ?? item.laboratoryName) || "",
                    indicatorsCount: item.indicatorsCount || 0,
                    quantityByTarification: 0,
                    sum: 0,
                    requests: [],
                };
            }
            groups[key].requests.push({
                departmentName: (item.departmentName ?? item.laboratoryName) || "",
                signedProtocols: item.signedProtocols || 0,
                quantityByTarification: (item.quantityByTarification ?? item.quantityByRequestTarification) || 0,
                sum: item.sum || 0,
            });
            groups[key].signedProtocols += item.signedProtocols || 0;
            groups[key].quantityByTarification += (item.quantityByTarification ?? item.quantityByRequestTarification) || 0;
            groups[key].sum += item.sum || 0;
        });
        return Object.values(groups);
    };

    const handleExcelExport = async () => {
        if (groupedData.length === 0) {
            alert("Нет данных для экспорта. Сначала сформируйте отчет.");
            return;
        }
        setExportLoading(true);
        try {
            const center10 = { alignment: { horizontal: "center" }, font: { sz: 10 } };
            const centerBold10 = { alignment: { horizontal: "center" }, font: { sz: 10, bold: true } };
            const wb = XLSX.utils.book_new();
            const cell = (text, style = center10) => ({ t: "s", v: text, s: style });
            const numberCell = (value, style = center10) => ({ t: "n", v: value, s: style });

            const aoa = [
                [
                    cell("№", centerBold10),
                    cell("Договор", centerBold10),
                    cell("Филиал", centerBold10),
                    cell("Районное отделение", centerBold10),
                    cell("Дата создания", centerBold10),
                    cell("Номер направления", centerBold10),
                    cell("Количество подписанных протоколов", centerBold10),
                    cell("Лаборатория", centerBold10),
                    cell("Количество показателей в направлении", centerBold10),
                    cell("Количество исследований в тарификаторе", centerBold10),
                    cell("Сумма", centerBold10),
                ],
            ];

            let rowIndex = 1;
            groupedData.forEach((group) => {
                group.requests.forEach((request, requestIndex) => {
                    aoa.push([
                        cell(requestIndex === 0 ? rowIndex.toString() : ""),
                        cell(requestIndex === 0 ? (group.governmentContractName || "") : ""),
                        cell(requestIndex === 0 ? (group.filialName || "") : ""),
                        cell(requestIndex === 0 ? (group.filialDivisionName || "") : ""),
                        cell(requestIndex === 0 ? (group.createdAt ? formatDate(group.createdAt, "DD.MM.YYYY") : "") : ""),
                        cell(requestIndex === 0 ? (group.directionNumber || "") : ""),
                        numberCell(request.signedProtocols || 0),
                        cell(request.departmentName || ""),
                        numberCell(requestIndex === 0 ? (group.indicatorsCount || 0) : 0),
                        numberCell(request.quantityByTarification || 0),
                        numberCell(safeRoundTo2(request.sum || 0)),
                    ]);
                });
                rowIndex++;
            });

            if (groupedData.length > 0) {
                const totalProtocolCount = sum(groupedData, (_) => _.signedProtocols || 0);
                const totalTarificationResearchCount = sum(groupedData, (_) => _.quantityByTarification || 0);
                const totalSum = safeRoundTo2(sum(groupedData, (_) => _.sum || 0));
                aoa.push([
                    cell(""),
                    cell("Всего:", centerBold10),
                    cell(""),
                    cell(""),
                    cell(""),
                    cell(""),
                    numberCell(totalProtocolCount),
                    cell(""),
                    cell(""),
                    numberCell(totalTarificationResearchCount),
                    numberCell(totalSum),
                ]);
            }

            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = [
                { width: 5 },
                { width: 20 },
                { width: 20 },
                { width: 25 },
                { width: 15 },
                { width: 20 },
                { width: 25 },
                { width: 20 },
                { width: 30 },
                { width: 25 },
                { width: 15 },
            ];
            XLSX.utils.book_append_sheet(wb, ws, "Исполнение КСЭК Направления");
            const fileName = `Исполнение_КСЭК_Направления_${formatDate(new Date(), MOMENT_FORMAT_DATE_DMYHM_DOTS)}.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error("Ошибка при экспорте:", error);
            alert("Ошибка при экспорте файла");
        } finally {
            setExportLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const [filialsData, departmentsData] = await Promise.all([
                    filialCRUD.search({ paging: { skip: 0, take: 1000 }, filter: { organizationId: { operand1: organizationIds.nce, operator: "equals" } } }),
                    departmentCRUD.search({ paging: { skip: 0, take: 1000 } }),
                ]);
                setFilials(filialsData?.result || []);
                setDepartments(departmentsData?.result || []);
            } catch (error) {
                console.error("Ошибка загрузки данных фильтров:", error);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        const loadFilialDivisions = async () => {
            if (filters.filialId) {
                try {
                    const filialDivisionsData = await filialDivisionCRUD.search({
                        paging: { skip: 0, take: 1000 },
                        filter: { filialId: { operand1: filters.filialId, operator: "equals" } },
                    });
                    setFilialDivisions(filialDivisionsData?.result || []);
                } catch (error) {
                    console.error("Ошибка загрузки районных отделений:", error);
                }
            } else {
                setFilialDivisions([]);
            }
        };
        loadFilialDivisions();
    }, [filters.filialId]);

    return (
        <DashboardLayout>
            <div style={{ padding: "20px" }}>
                <h2>Исполнение КСЭК Направления</h2>
                <Divider />
                <Grid container spacing={2} style={{ marginBottom: "20px" }}>
                    <Grid item xs={12} md={2} style={{ marginTop: -8 }}>
                        <ContractSelect
                            label={"Договор КСЭК"}
                            year={(new Date()).getFullYear()}
                            fullWidth
                            margin="dense"
                            value={filters.governmentContractId}
                            onChange={(val) => setFilters({ ...filters, governmentContractId: val })}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Филиал</InputLabel>
                            <Select
                                value={filters.filialId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        filialId: e.target.value,
                                        filialDivisionId: null,
                                        departmentId: null,
                                    });
                                }}
                            >
                                <MenuItem value="">Все филиалы</MenuItem>
                                {filials.map((filial) => (
                                    <MenuItem key={filial.id} value={filial.id}>
                                        {filial.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Районное отделение</InputLabel>
                            <Select
                                value={filters.filialDivisionId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        filialDivisionId: e.target.value
                                    });
                                }}
                            >
                                <MenuItem value="">Все районные отделения</MenuItem>
                                {filialDivisions.map((division) => (
                                    <MenuItem key={division.id} value={division.id}>
                                        {division.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Лаборатория</InputLabel>
                            <Select
                                value={filters.departmentId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        departmentId: e.target.value
                                    });
                                }}
                            >
                                <MenuItem value="">Все лаборатории</MenuItem>
                                {departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id}>
                                        {department.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <div>
                            <FilterDatePicker
                                label="Дата создания с"
                                value={query.filter.createdAt.operand1}
                                handleFunction={(date) => {
                                    setQuery({
                                        ...query,
                                        filter: {
                                            ...query.filter,
                                            createdAt: {
                                                ...query.filter.createdAt,
                                                operand1: date
                                            }
                                        }
                                    });
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <div>
                            <FilterDatePicker
                                label="Дата создания по"
                                value={query.filter.createdAt.operand2}
                                handleFunction={(date) => {
                                    setQuery({
                                        ...query,
                                        filter: {
                                            ...query.filter,
                                            createdAt: {
                                                ...query.filter.createdAt,
                                                operand2: date
                                            }
                                        }
                                    });
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
                <Stack direction="row" spacing={2} style={{ marginBottom: "20px" }}>
                    <Button variant="contained" onClick={fetchAsync} disabled={loading}>
                        {loading ? "Загрузка..." : "Обновить"}
                    </Button>
                    <LoadingButton variant="contained" onClick={handleExcelExport} loading={exportLoading} disabled={data.length === 0}>
                        Экспорт в Excel
                    </LoadingButton>
                </Stack>

                <table className="ant-table">
                    <thead className="ant-table-thead">
                        <tr className="ant-table-row">
                            <th className="ant-table-cell">№</th>
                            <th className="ant-table-cell">Договор</th>
                            <th className="ant-table-cell">Филиал</th>
                            <th className="ant-table-cell">Районное отделение</th>
                            <th className="ant-table-cell">Дата создания</th>
                            <th className="ant-table-cell">Номер направления</th>
                            <th className="ant-table-cell">Количество подписанных протоколов</th>
                            <th className="ant-table-cell">Лаборатория</th>
                            <th className="ant-table-cell">Количество показателей в направлении</th>
                            <th className="ant-table-cell">Количество исследований в тарификаторе</th>
                            <th className="ant-table-cell">Сумма</th>
                        </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                        {groupedData && groupedData.map((group, groupIndex) => {
                            return group.requests.map((request, requestIndex) => (
                                <tr key={`${groupIndex}-${requestIndex}`} className="ant-table-row" style={{ backgroundColor: "#fafafa" }}>
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{groupIndex + 1}</td> : null}
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.governmentContractName || ""}</td> : null}
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.filialName || ""}</td> : null}
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.filialDivisionName || ""}</td> : null}
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.createdAt ? formatDate(group.createdAt, "DD.MM.YYYY") : ""}</td> : null}
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.directionNumber || ""}</td> : null}
                                    <td className="ant-table-cell">{request.signedProtocols || 0}</td>
                                    <td className="ant-table-cell">{request.departmentName || ""}</td>
                                    {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length}>{group.indicatorsCount || 0}</td> : null}
                                    <td className="ant-table-cell">{request.quantityByTarification || 0}</td>
                                    <td className="ant-table-cell">{safeRoundTo2(request.sum || 0)}</td>
                                </tr>
                            ));
                        })}
                        {groupedData.length > 0 && (
                            <tr className="ant-table-row" style={{ backgroundColor: "#3cb37188" }}>
                                <td></td>
                                <td>Всего:</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="ant-table-cell">{sum(groupedData, (_) => _.signedProtocols || 0)}</td>
                                <td></td>
                                <td></td>
                                <td className="ant-table-cell">{sum(groupedData, (_) => _.quantityByTarification || 0)}</td>
                                <td className="ant-table-cell">{safeRoundTo2(sum(groupedData, (_) => _.sum || 0))}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </DashboardLayout>
    );
};

export default DirectionsSummary; 